import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { supabase } from '../../lib/supabase';
import type { PortalClass, Announcement } from '../../types/portal';
import {
  ChevronRight,
  ChevronLeft,
  BookOpen,
  AlertCircle,
  X,
  Bell,
  CreditCard,
  Award,
  Clock,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  GraduationCap,
} from 'lucide-react';
import CourseCover from '../../components/CourseCover';
import { NEW_WINDOW_DAYS } from '../../lib/portalHelpers';

interface ClassWithProgress {
  cls: PortalClass;
  totalLessons: number;
  completedLessons: number;
  latestRecordingAt: number;
  newLessons: number;
  resumeRecordingId: string | null;
}

interface LatestRecording {
  id: string;
  class_id: string;
  title: string;
  className: string;
  dateIso: string;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'heute';
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 30) return `vor ${Math.floor(days / 7)} Wochen`;
  return `vor ${Math.floor(days / 30)} Monaten`;
}

// ─── KPI stat tile ──────────────────────────────────────────────
function StatTile({
  icon: Icon,
  value,
  label,
  onClick,
  alert = false,
  className = '',
}: {
  icon: React.ElementType;
  value: React.ReactNode;
  label: string;
  onClick?: () => void;
  alert?: boolean;
  className?: string;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`bg-white dark:bg-neutral-900 rounded-2xl border p-4 flex flex-col gap-2.5 text-left transition-all duration-200 ${
        alert
          ? 'border-[#970000]/25 dark:border-red-500/40'
          : 'border-gray-200 dark:border-neutral-800'
      } ${onClick ? 'hover:border-[#970000]/40 dark:hover:border-red-500/50 hover:shadow-[0_6px_24px_rgba(151,0,0,0.08)] cursor-pointer' : ''} ${className}`}
    >
      <div
        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
          alert
            ? 'bg-[#970000]/10 dark:bg-red-500/15'
            : 'bg-gray-50 dark:bg-neutral-800'
        }`}
      >
        <Icon
          className={`h-4 w-4 ${
            alert ? 'text-[#970000] dark:text-red-400' : 'text-[#970000] dark:text-red-400'
          }`}
        />
      </div>
      <div className="min-w-0">
        <p
          className={`text-xl font-bold leading-tight truncate ${
            alert ? 'text-[#970000] dark:text-red-400' : 'text-gray-900 dark:text-neutral-100'
          }`}
        >
          {value}
        </p>
        <p className="text-[11px] text-gray-400 dark:text-neutral-500 leading-snug">{label}</p>
      </div>
    </Tag>
  );
}

// ─── Quick-access navigation tile ──────────────────────────────
function NavTile({
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 hover:border-[#970000]/40 dark:hover:border-red-500/50 hover:shadow-[0_6px_24px_rgba(151,0,0,0.08)] transition-all duration-200 text-left p-4 flex items-center gap-3 group"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#970000] dark:text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 dark:text-neutral-100">{title}</p>
        <p className="text-xs text-gray-400 dark:text-neutral-500 line-clamp-1">{subtitle}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-300 dark:text-neutral-600 shrink-0 group-hover:text-[#970000] dark:group-hover:text-red-400 transition-colors" />
    </button>
  );
}

// ─── Important announcement strip ──────────────────────────────
function AnnouncementBanner({
  ann,
  onDismiss,
}: {
  ann: Announcement;
  onDismiss: () => void;
}) {
  return (
    <div className="relative bg-[#970000] text-white rounded-2xl flex items-center group">
      <Link
        to="/dashboard/ankuendigungen"
        className="flex-1 px-4 py-3 flex items-center gap-3 hover:bg-[#7a0000] rounded-2xl transition-colors min-w-0"
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        <div className="flex-1 min-w-0 pr-8">
          <p className="text-xs font-bold tracking-wider uppercase opacity-75">Wichtig</p>
          <p className="text-sm font-semibold line-clamp-1">{ann.title}</p>
        </div>
      </Link>
      <button
        onClick={e => { e.stopPropagation(); onDismiss(); }}
        className="absolute right-3 p-1 rounded-lg hover:bg-white/15 shrink-0"
        aria-label="Ankündigung ausblenden"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Cockpit panel shell ────────────────────────────────────────
function PanelCard({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden flex flex-col">
      <div className="px-5 pt-4 pb-3 border-b border-gray-200 dark:border-neutral-800">
        <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
          {label}
        </p>
        <h3 className="text-base font-bold text-gray-900 dark:text-neutral-100">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CourseCard({ item, onClick }: { item: ClassWithProgress; onClick: () => void }) {
  const { cls, totalLessons, completedLessons, newLessons } = item;
  const pct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const isArchived = !!cls.archived_at;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white dark:bg-neutral-900 rounded-[20px] border border-gray-200 dark:border-neutral-800 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-[#970000]/40 dark:hover:border-red-500/50 hover:shadow-[0_6px_24px_rgba(151,0,0,0.08)] transition-all duration-200 text-left overflow-hidden group"
    >
      <div className="flex items-center gap-4 p-4 pb-3">
        <CourseCover cls={cls} width={100} height={72} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 dark:text-neutral-100 text-sm leading-snug line-clamp-2 mb-2">
            {cls.title}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {(cls.category || cls.level) && (
              <span className="text-[10px] font-bold tracking-wider uppercase bg-[#111827] dark:bg-neutral-100 text-white dark:text-neutral-900 px-2 py-0.5 rounded-md">
                {cls.category ?? cls.level}
              </span>
            )}
            {isArchived ? (
              <span className="text-[10px] font-semibold text-[#5f5148] dark:text-neutral-400 bg-gray-50 dark:bg-neutral-800 border border-[#ddd6cf] dark:border-neutral-700 px-2 py-0.5 rounded-md">
                Fertig
              </span>
            ) : (
              <span className="text-[10px] font-semibold text-[#5f5148] dark:text-neutral-400 border border-gray-200 dark:border-neutral-800 px-2 py-0.5 rounded-md">
                Aktiv
              </span>
            )}
            {newLessons > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#970000] px-2 py-0.5 rounded-md">
                {newLessons} Neu
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 dark:text-neutral-500">
              {completedLessons}/{totalLessons} Lektionen
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-[#5f5148] dark:text-neutral-400">{pct}%</span>
              <ChevronRight className="h-4 w-4 text-gray-300 dark:text-neutral-600 group-hover:text-[#970000] dark:group-hover:text-red-400 transition-colors" />
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-gray-100 dark:bg-neutral-800">
        <div
          className="h-full bg-[#970000] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </button>
  );
}

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-baseline gap-2 mb-3">
      <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">{title}</h2>
      <span className="text-xl font-bold text-gray-300 dark:text-neutral-600">{count}</span>
    </div>
  );
}

const LEGACY_DISMISSED_KEY = 'arabicroots:dismissed_announcements';

const StudentDashboardHome: React.FC = () => {
  const { user, profile } = useStudentAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ClassWithProgress[]>([]);
  const [latestRecording, setLatestRecording] = useState<LatestRecording | null>(null);
  const [importantAnnouncement, setImportantAnnouncement] = useState<Announcement | null>(null);
  const [latestAnnouncements, setLatestAnnouncements] = useState<Announcement[]>([]);
  const [hasPaymentPlan, setHasPaymentPlan] = useState(false);
  const [openPayments, setOpenPayments] = useState(0);
  const [nextPaymentDue, setNextPaymentDue] = useState<string | null>(null);
  const [hasOverduePayment, setHasOverduePayment] = useState(false);
  const [certificatesCount, setCertificatesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Load dismissed announcement IDs from DB
  const loadDismissedIds = useCallback(async (studentId: string): Promise<Set<string>> => {
    try {
      const { data } = await supabase
        .from('announcement_reads')
        .select('announcement_id')
        .eq('student_id', studentId);
      const dbIds = new Set<string>((data ?? []).map((r: { announcement_id: string }) => r.announcement_id));

      // Migrate any existing localStorage entries to DB on first run
      const legacyRaw = localStorage.getItem(LEGACY_DISMISSED_KEY);
      if (legacyRaw) {
        try {
          const legacyIds: string[] = JSON.parse(legacyRaw);
          const toMigrate = legacyIds.filter(id => !dbIds.has(id));
          if (toMigrate.length > 0) {
            await supabase.from('announcement_reads').upsert(
              toMigrate.map(id => ({ student_id: studentId, announcement_id: id })),
              { onConflict: 'student_id,announcement_id', ignoreDuplicates: true }
            );
            toMigrate.forEach(id => dbIds.add(id));
          }
          localStorage.removeItem(LEGACY_DISMISSED_KEY);
        } catch { /* ignore migration errors */ }
      }

      return dbIds;
    } catch {
      return new Set();
    }
  }, []);

  useEffect(() => {
    if (!profile) return;
    const studentId = user?.id;
    if (!studentId) {
      setLoading(false);
      return;
    }
    const isAdmin = profile.role === 'admin';

    const load = async () => {
      try {
        const { data: memberships, error: membershipsError } = await supabase
          .from('portal_class_members')
          .select('class_id')
          .eq('student_id', studentId)
          .eq('is_active', true);

        if (membershipsError) throw membershipsError;

        const classIds = Array.from(new Set((memberships ?? []).map((m: { class_id: string }) => m.class_id)));

        // Sichtbare Ankündigungen: eigene Klassen + globale
        const announcementFilter = `class_id.is.null${classIds.length ? `,class_id.in.(${classIds.join(',')})` : ''}`;

        const { data: classesData } = classIds.length
          ? await supabase.from('portal_classes').select('*').in('id', classIds)
          : { data: [] as PortalClass[] };

        const [
          { data: recordingsData },
          { data: progressData },
          { data: annData },
          { data: lastPositionData },
          { data: latestAnnData },
          { data: plansData },
          { count: certCount },
          dismissedIds,
        ] = await Promise.all([
          classIds.length
            ? supabase
                .from('recordings')
                .select('id, class_id, title, sort_order, recorded_at, created_at')
                .in('class_id', classIds)
                .eq('is_visible', true)
            : Promise.resolve({ data: [] as never[] }),
          supabase.from('recording_progress').select('recording_id').eq('student_id', profile.id),
          supabase
            .from('announcements')
            .select('*')
            .eq('is_important', true)
            .order('created_at', { ascending: false })
            .limit(5),
          supabase
            .from('recording_positions')
            .select('recording_id, updated_at')
            .eq('student_id', profile.id)
            .order('updated_at', { ascending: false }),
          supabase
            .from('announcements')
            .select('*')
            .or(announcementFilter)
            .order('created_at', { ascending: false })
            .limit(3),
          isAdmin
            ? Promise.resolve({ data: [] as { id: string }[] })
            : supabase
                .from('student_payment_plans')
                .select('id')
                .eq('student_id', studentId)
                .eq('status', 'active'),
          supabase
            .from('certificates')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', studentId),
          loadDismissedIds(studentId),
        ]);

        setLatestAnnouncements((latestAnnData ?? []) as Announcement[]);
        setCertificatesCount(certCount ?? 0);

        // Offene Zahlungen (abhängig vom aktiven Plan)
        const planIds = ((plansData ?? []) as { id: string }[]).map(p => p.id);
        setHasPaymentPlan(planIds.length > 0);
        if (planIds.length > 0) {
          const { data: openPays } = await supabase
            .from('student_payments')
            .select('status, due_date')
            .in('plan_id', planIds)
            .in('status', ['open', 'overdue']);
          const unpaid = (openPays ?? []) as { status: string; due_date: string }[];
          const todayStr = new Date().toISOString().slice(0, 10);
          setOpenPayments(unpaid.length);
          setHasOverduePayment(unpaid.some(p => p.due_date < todayStr));
          setNextPaymentDue(unpaid.map(p => p.due_date).sort()[0] ?? null);
        }

        const completedSet = new Set(
          (progressData ?? []).map((p: { recording_id: string }) => p.recording_id)
        );

        type RecordingRow = {
          id: string;
          class_id: string;
          title: string;
          sort_order: number | null;
          recorded_at: string | null;
          created_at: string;
        };
        const recordings = (recordingsData ?? []) as RecordingRow[];

        // Zuletzt angesehene Positionen (absteigend nach updated_at) → Map
        const positions = (lastPositionData ?? []) as { recording_id: string; updated_at: string }[];
        const posTime = new Map<string, number>();
        for (const p of positions) {
          if (!posTime.has(p.recording_id)) posTime.set(p.recording_id, new Date(p.updated_at).getTime());
        }

        // Aufzeichnungen je Kurs + Sortierung wie auf der Kursseite
        const recordingsByClass: Record<string, RecordingRow[]> = {};
        for (const r of recordings) (recordingsByClass[r.class_id] ??= []).push(r);
        const cmpLessonOrder = (a: RecordingRow, b: RecordingRow) => {
          const sa = a.sort_order ?? Number.MAX_SAFE_INTEGER;
          const sb = b.sort_order ?? Number.MAX_SAFE_INTEGER;
          if (sa !== sb) return sa - sb;
          const ra = new Date(a.recorded_at ?? a.created_at).getTime();
          const rb = new Date(b.recorded_at ?? b.created_at).getTime();
          if (ra !== rb) return ra - rb;
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        };
        // Fortsetzungs-Lektion je Kurs: zuletzt angesehene, sonst erste offene, sonst erste
        const resumeFor = (classId: string): string | null => {
          const list = recordingsByClass[classId] ?? [];
          if (list.length === 0) return null;
          const watched = list.filter(r => posTime.has(r.id));
          if (watched.length > 0) {
            watched.sort((a, b) => posTime.get(b.id)! - posTime.get(a.id)!);
            return watched[0].id;
          }
          const ordered = [...list].sort(cmpLessonOrder);
          return (ordered.find(r => !completedSet.has(r.id)) ?? ordered[0]).id;
        };

        const newCutoff = Date.now() - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
        const perClass: Record<string, { ids: string[]; latestTs: number; newLessons: number }> = {};
        for (const r of recordings) {
          if (!perClass[r.class_id]) perClass[r.class_id] = { ids: [], latestTs: 0, newLessons: 0 };
          perClass[r.class_id].ids.push(r.id);
          const ts = new Date(r.recorded_at ?? r.created_at).getTime();
          if (ts > perClass[r.class_id].latestTs) perClass[r.class_id].latestTs = ts;
          if (new Date(r.created_at).getTime() > newCutoff && !completedSet.has(r.id)) {
            perClass[r.class_id].newLessons += 1;
          }
        }

        const classMap = Object.fromEntries(
          ((classesData ?? []) as PortalClass[]).map(c => [c.id, c])
        );

        const activeRecordings = recordings.filter(
          r => classMap[r.class_id] && !classMap[r.class_id].archived_at
        );
        const recordingMap = Object.fromEntries(activeRecordings.map(r => [r.id, r]));

        // Global zuletzt angeschaut (für Reihenfolge & "Zum Kurs"), sonst neueste Aufzeichnung
        const lastWatched = positions[0] ? recordingMap[positions[0].recording_id] : null;

        const candidate = lastWatched ?? (() => {
          const sorted = [...activeRecordings].sort(
            (a, b) =>
              new Date(b.recorded_at ?? b.created_at).getTime() -
              new Date(a.recorded_at ?? a.created_at).getTime()
          );
          return sorted[0] ?? null;
        })();

        if (candidate) {
          setLatestRecording({
            id: candidate.id,
            class_id: candidate.class_id,
            title: candidate.title,
            className: classMap[candidate.class_id]?.title ?? '',
            dateIso: candidate.recorded_at ?? candidate.created_at,
          });
        }

        const importants = (annData ?? []) as Announcement[];
        const firstAnn = importants.find(a => !dismissedIds.has(a.id));
        if (firstAnn) setImportantAnnouncement(firstAnn);

        const result: ClassWithProgress[] = (classesData ?? []).map(c => {
          const cls = c as PortalClass;
          const info = perClass[cls.id] ?? { ids: [], latestTs: 0, newLessons: 0 };
          const completed = info.ids.filter(id => completedSet.has(id)).length;
          return {
            cls,
            totalLessons: info.ids.length,
            completedLessons: completed,
            latestRecordingAt: info.latestTs,
            newLessons: info.newLessons,
            resumeRecordingId: resumeFor(cls.id),
          };
        });

        result.sort((a, b) => {
          const aA = !!a.cls.archived_at;
          const bA = !!b.cls.archived_at;
          if (aA !== bA) return aA ? 1 : -1;
          if (a.latestRecordingAt !== b.latestRecordingAt) {
            return b.latestRecordingAt - a.latestRecordingAt;
          }
          return a.cls.title.localeCompare(b.cls.title, 'de');
        });

        setCourses(result);
      } catch (err) {
        if (import.meta.env.DEV) console.error('StudentDashboardHome load error:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.id, profile?.id, profile?.role, loadDismissedIds]);

  const dismissAnnouncement = async () => {
    if (!importantAnnouncement || !user?.id) return;
    const annId = importantAnnouncement.id;
    setImportantAnnouncement(null);
    try {
      await supabase.from('announcement_reads').upsert(
        { student_id: user.id, announcement_id: annId },
        { onConflict: 'student_id,announcement_id', ignoreDuplicates: true }
      );
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to persist announcement dismiss:', err);
    }
  };

  const firstName = profile?.first_name || profile?.display_name || 'Schüler';
  const active = courses.filter(c => !c.cls.archived_at);
  const archived = courses.filter(c => !!c.cls.archived_at);

  // Cockpit-Kennzahlen (nur aktive Kurse)
  const totalActive = active.reduce((s, c) => s + c.totalLessons, 0);
  const completedActive = active.reduce((s, c) => s + c.completedLessons, 0);
  const overallPct = totalActive > 0 ? Math.round((completedActive / totalActive) * 100) : 0;
  const newLessonsTotal = active.reduce((s, c) => s + c.newLessons, 0);
  const scheduleRows = active.filter(c => !!c.cls.schedule);

  // Kurs für den Schnellzugriff "Zum Kurs": zuletzt gelernter, sonst aktuellster aktiver Kurs
  const primaryCourse =
    active.find(c => c.cls.id === latestRecording?.class_id)?.cls ?? active[0]?.cls ?? null;

  // "Weiter lernen": aktive Kurse, zuletzt gelernter zuerst
  const lastIdx = latestRecording ? active.findIndex(c => c.cls.id === latestRecording.class_id) : -1;
  const activeOrdered =
    lastIdx > 0 ? [active[lastIdx], ...active.slice(0, lastIdx), ...active.slice(lastIdx + 1)] : active;

  const scrollSlider = (dir: number) => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };

  // Klick auf eine "Weiter lernen"-Karte: direkt in die Fortsetzungs-Lektion (Video)
  const openLearning = (item: ClassWithProgress) => {
    if (item.resumeRecordingId) {
      navigate(`/dashboard/kurse/${item.cls.id}/lektion/${item.resumeRecordingId}`);
    } else {
      navigate(`/dashboard/kurse/${item.cls.id}`);
    }
  };

  // Schnellzugriff-Kacheln (Zum Kurs nur, wenn ein aktiver Kurs existiert)
  const navItems: { key: string; icon: React.ElementType; title: string; subtitle: string; onClick: () => void }[] = [];
  if (primaryCourse) {
    navItems.push({
      key: 'course',
      icon: GraduationCap,
      title: 'Zum Kurs',
      subtitle: primaryCourse.title,
      onClick: () => navigate(`/dashboard/kurse/${primaryCourse.id}`),
    });
  }
  navItems.push({
    key: 'lernapp',
    icon: BookOpen,
    title: 'Lernapp',
    subtitle: 'Vokabeln & Wörterbuch',
    onClick: () => navigate('/dashboard/lernapp'),
  });
  navItems.push({
    key: 'certificates',
    icon: Award,
    title: 'Zertifikate',
    subtitle:
      certificatesCount > 0
        ? `${certificatesCount} ${certificatesCount === 1 ? 'Zertifikat' : 'Zertifikate'}`
        : 'Deine Abschlüsse',
    onClick: () => navigate('/dashboard/zertifikate'),
  });
  const navGrid = (
    <div className={`grid gap-3 grid-cols-1 ${navItems.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      {navItems.map(n => (
        <NavTile key={n.key} icon={n.icon} title={n.title} subtitle={n.subtitle} onClick={n.onClick} />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-4 w-32 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse mb-2" />
          <div className="h-8 w-48 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
          ))}
        </div>
        <div className="h-24 bg-white dark:bg-neutral-900 rounded-[20px] border border-gray-200 dark:border-neutral-800 animate-pulse" />
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-28 bg-white dark:bg-neutral-900 rounded-[20px] border border-gray-200 dark:border-neutral-800 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {/* Greeting */}
      <div>
        <p className="text-xs font-bold tracking-[0.14em] uppercase text-[#970000]/60 dark:text-red-400 mb-1">
          Willkommen zurück
        </p>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Hallo, {firstName}</h2>
      </div>

      {/* Important announcement (top, dismissable) */}
      {importantAnnouncement && (
        <AnnouncementBanner ann={importantAnnouncement} onDismiss={dismissAnnouncement} />
      )}

      {courses.length === 0 ? (
        <>
          <div className="bg-white dark:bg-neutral-900 rounded-[20px] border border-gray-200 dark:border-neutral-800 p-10 text-center">
            <BookOpen className="h-10 w-10 text-[#d6cec5] dark:text-neutral-600 mx-auto mb-3" />
            <p className="font-semibold text-gray-900 dark:text-neutral-100 mb-1">Noch keine Kurse</p>
            <p className="text-sm text-gray-400 dark:text-neutral-500">
              Du wirst von deinem Admin einem Kurs zugeordnet.
            </p>
          </div>
          {navGrid}
        </>
      ) : (
        <>
          {/* ── Kennzahlen ── */}
          <div className={`grid gap-3 grid-cols-2 ${hasPaymentPlan ? 'lg:grid-cols-3' : ''}`}>
            <StatTile
              icon={TrendingUp}
              value={`${overallPct}%`}
              label={`Gesamtfortschritt · ${completedActive}/${totalActive} Lektionen`}
            />
            <StatTile
              icon={Sparkles}
              value={newLessonsTotal}
              label={newLessonsTotal === 1 ? 'Neue Lektion' : 'Neue Lektionen'}
            />
            {hasPaymentPlan && (() => {
              let value: React.ReactNode = 'OK';
              let label = 'Zahlung in Ordnung';
              if (hasOverduePayment) {
                value = 'Fällig';
                label = 'Zahlung überfällig — jetzt begleichen';
              } else if (openPayments > 0 && nextPaymentDue) {
                const days = Math.round(
                  (new Date(nextPaymentDue + 'T00:00:00').getTime() - new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00').getTime()) / 86_400_000
                );
                value = days <= 0 ? 'Heute' : days === 1 ? '1 Tag' : `${days} Tage`;
                label = `Nächste Zahlung am ${new Date(nextPaymentDue + 'T00:00:00').toLocaleDateString('de-DE')}`;
              } else if (openPayments > 0) {
                value = openPayments;
                label = openPayments === 1 ? 'Offene Zahlung' : 'Offene Zahlungen';
              }
              return (
                <StatTile
                  icon={openPayments > 0 ? CreditCard : CheckCircle2}
                  value={value}
                  label={label}
                  alert={hasOverduePayment}
                  onClick={() => navigate('/dashboard/zahlungen')}
                  className="col-span-2 lg:col-span-1"
                />
              );
            })()}
          </div>

          {/* ── Schnellzugriff ── */}
          {navGrid}

          {/* ── Weiter lernen (Kurskarten, Slider bei mehreren) ── */}
          {active.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Weiter lernen</h2>
                  <span className="text-xl font-bold text-gray-300 dark:text-neutral-600">{active.length}</span>
                </div>
                {active.length > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => scrollSlider(-1)}
                      aria-label="Zurück"
                      className="w-8 h-8 rounded-full border border-gray-200 dark:border-neutral-800 flex items-center justify-center text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-[#970000] dark:hover:text-red-400 transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => scrollSlider(1)}
                      aria-label="Weiter"
                      className="w-8 h-8 rounded-full border border-gray-200 dark:border-neutral-800 flex items-center justify-center text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-[#970000] dark:hover:text-red-400 transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {active.length === 1 ? (
                <CourseCard item={activeOrdered[0]} onClick={() => openLearning(activeOrdered[0])} />
              ) : (
                <div
                  ref={sliderRef}
                  className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  {activeOrdered.map(item => (
                    <div key={item.cls.id} className="snap-start shrink-0 w-full">
                      <CourseCard item={item} onClick={() => openLearning(item)} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Two-column panels: schedule + announcements ── */}
          {(scheduleRows.length > 0 || latestAnnouncements.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {scheduleRows.length > 0 && (
                <PanelCard label="Stundenplan" title="Deine Unterrichtszeiten">
                  <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
                    {scheduleRows.map(({ cls }) => (
                      <li key={cls.id}>
                        <button
                          onClick={() => navigate(`/dashboard/kurse/${cls.id}`)}
                          className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <div className="shrink-0 w-8 h-8 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center">
                            <Clock className="h-3.5 w-3.5 text-[#970000] dark:text-red-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 line-clamp-1">
                              {cls.title}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-neutral-500 line-clamp-1">
                              {cls.schedule}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </PanelCard>
              )}

              {latestAnnouncements.length > 0 && (
                <PanelCard label="Neuigkeiten" title="Letzte Ankündigungen">
                  <ul className="divide-y divide-gray-200 dark:divide-neutral-800 flex-1">
                    {latestAnnouncements.map(a => (
                      <li key={a.id}>
                        <Link
                          to={`/dashboard/ankuendigungen?a=${a.id}`}
                          className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <div
                            className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
                              a.is_important
                                ? 'bg-[#970000]/10 dark:bg-red-500/15'
                                : 'bg-gray-50 dark:bg-neutral-800'
                            }`}
                          >
                            {a.is_important ? (
                              <AlertCircle className="h-3.5 w-3.5 text-[#970000] dark:text-red-400" />
                            ) : (
                              <Bell className="h-3.5 w-3.5 text-gray-400 dark:text-neutral-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 line-clamp-1">
                              {a.title}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-neutral-500">
                              {relativeTime(a.created_at)}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/dashboard/ankuendigungen"
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-[#970000] dark:text-red-400 hover:bg-gray-50 dark:hover:bg-neutral-800 border-t border-gray-200 dark:border-neutral-800 transition-colors"
                  >
                    Alle Ankündigungen
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </PanelCard>
              )}
            </div>
          )}

          {/* ── Abgeschlossene Werke ── */}
          {archived.length > 0 && (
            <section>
              <SectionHeader title="Abgeschlossene Werke" count={archived.length} />
              <div className="space-y-3">
                {archived.map(item => (
                  <CourseCard
                    key={item.cls.id}
                    item={item}
                    onClick={() => navigate(`/dashboard/kurse/${item.cls.id}`)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default StudentDashboardHome;
